import { useCallback } from 'react';
import LangEnum from '../lang/LangEnum';
import { useTranslation } from '../modules/translation/TranslationContext';

export type AppExceptionResponseData<T = any> = {
  code: LangEnum;
  message: string;
  data?: Record<string, T>;
};

export type AppExceptionResponse<T = any> = {
  data: AppExceptionResponseData<T>;
  ok: boolean;
};

export const isAppExceptionResponseData = (data: unknown): data is AppExceptionResponseData =>
  !!data && typeof data === 'object' && 'code' in data && 'message' in data;

export const resolveExceptionMessage = (data: unknown) => {
  if (!data || typeof data !== 'object' || !('message' in data)) {
    return '';
  }

  const { message } = data as { message?: string | string[] };

  return Array.isArray(message) ? message.join(', ') : (message ?? '');
};

/**
 * Função que lida com AppException
 *
 * AppException é o tipo de erro internacionalizado que vem do backend
 */
export default function useHandleAppException() {
  const { translate } = useTranslation();

  const handleError = useCallback(
    <T = any>({ code, data, message }: AppExceptionResponseData<T>) => {
      if (code) {
        return translate(code, data, message);
      }
      return message;
    },
    [translate],
  );

  const handleErrorResponse = useCallback(
    <T = any>(response: AppExceptionResponse<T>) => {
      const { code, data, message } = response.data;
      return handleError<T>({ code, data, message });
    },
    [handleError],
  );

  const handleErrorWithFallback = (responseData: unknown, fallbackMessage: string) => {
    if (!isAppExceptionResponseData(responseData)) {
      return fallbackMessage;
    }

    return handleError({
      code: responseData.code,
      data: responseData.data,
      message: fallbackMessage,
    });
  };

  return { handleError, handleErrorResponse, handleErrorWithFallback };
}
